import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus } from 'lucide-react';
import { type FormEvent, type JSX, useState } from 'react';

import { ActivationLinkNotice } from '../../../components/activation-link-notice';
import { listContacts, listSites } from '../../../customers/api';
import { formatCalendarDate } from '../../../customers/format-calendar-date';
import {
  PORTAL_ROLE_LABELS,
  invitePortalAccess,
  listPortalAccess,
  mapPortalAccessError,
  revokePortalAccess,
  updatePortalAccess,
  type InvitePortalAccessResult,
  type PortalAccessSummary,
  type UpdatePortalAccessInput,
} from './accesos/api';
import { PortalAccessInviteModal } from './portal-access-invite-modal';

export interface CustomerPortalAccessTabProps {
  customerId: string;
}

type PortalAccessStatus = 'pending' | 'active' | 'expired';

function statusOf(access: PortalAccessSummary, now: Date): PortalAccessStatus {
  if (access.invitationStatus === 'PENDING') {
    return 'pending';
  }

  if (access.validTo !== null && new Date(access.validTo) <= now) {
    return 'expired';
  }

  return 'active';
}

const STATUS_LABELS: Record<PortalAccessStatus, string> = {
  pending: 'Invitación pendiente',
  active: 'Activo',
  expired: 'Vencido',
};

const STATUS_BADGE_VARIANT: Record<PortalAccessStatus, 'neutral' | 'success' | 'danger'> = {
  pending: 'neutral',
  active: 'success',
  expired: 'danger',
};

type ModalState =
  | { mode: 'invite' }
  | { mode: 'edit'; access: PortalAccessSummary }
  | { mode: 'activation-link'; result: InvitePortalAccessResult }
  | null;

/**
 * `portal-access/spec.md` → "Pestaña 'Accesos al portal' en la ficha de
 * cliente". Gated by `customer.portal_access.manage`
 * (`portal-access-permissions.ts`) at the call site
 * (`customer-detail-page.tsx`), not inside this component: a tab the user
 * cannot see should never mount its data queries.
 */
export function CustomerPortalAccessTab({ customerId }: CustomerPortalAccessTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<ModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const accessQuery = useQuery({
    queryKey: ['customer-portal-access', customerId],
    queryFn: () => listPortalAccess(customerId),
  });

  const contactsQuery = useQuery({
    queryKey: ['customer-contacts', customerId],
    queryFn: () => listContacts(customerId),
  });

  const sitesQuery = useQuery({
    queryKey: ['customer-sites', customerId],
    queryFn: () => listSites(customerId),
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['customer-portal-access', customerId] });
  }

  const inviteMutation = useMutation({
    mutationFn: (input: Parameters<typeof invitePortalAccess>[1]) =>
      invitePortalAccess(customerId, input),
    onSuccess: async (result) => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });

      // A `NOT_SENT` outcome keeps the link in this modal state only, so it is
      // gone when the modal closes; the mutation cache is reset for the same reason.
      if (result.delivery.status === 'SENT') {
        setModal(null);
        toast('success', 'Invitación enviada');
      } else {
        setModal({ mode: 'activation-link', result });
      }

      inviteMutation.reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ accessId, input }: { accessId: string; input: UpdatePortalAccessInput }) =>
      updatePortalAccess(customerId, accessId, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Acceso actualizado');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (accessId: string) => revokePortalAccess(customerId, accessId),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Acceso revocado');
    },
    onError: (error) => setActionError(mapPortalAccessError(error)),
  });

  if (accessQuery.isPending || contactsQuery.isPending || sitesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (accessQuery.isError || contactsQuery.isError || sitesQuery.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar los accesos al portal">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void accessQuery.refetch();
              void contactsQuery.refetch();
              void sitesQuery.refetch();
            }}
          >
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const accessList = accessQuery.data;
  const sites = sitesQuery.data;
  const invitableContacts = contactsQuery.data.filter((contact) => !contact.hasPortalUser);
  const siteNameById = new Map(sites.map((site) => [site.id, site.name] as const));
  const now = new Date();

  const inviteFormError = mapPortalAccessError(inviteMutation.error);
  const editFormError = mapPortalAccessError(updateMutation.error);

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Accesos al portal</h2>
        <Button icon={Plus} onClick={() => setModal({ mode: 'invite' })}>
          Invitar al portal
        </Button>
      </div>

      {accessList.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="Esta empresa todavía no tiene usuarios del portal invitados."
        />
      ) : (
        <Table>
          <caption className="sr-only">Accesos al portal de la empresa</caption>
          <thead>
            <tr>
              <TableHeader>Usuario</TableHeader>
              <TableHeader>Rol</TableHeader>
              <TableHeader>Sedes</TableHeader>
              <TableHeader>Tope diario</TableHeader>
              <TableHeader>Vigencia</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {accessList.map((access) => {
              const status = statusOf(access, now);

              return (
                <TableRow key={access.id}>
                  <TableCell className="align-top">
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900">{access.fullName}</span>
                      <span className="text-xs text-slate-500">{access.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    {access.roleCode === null ? '—' : PORTAL_ROLE_LABELS[access.roleCode]}
                  </TableCell>
                  <TableCell className="align-top">
                    {access.siteIds.length === 0
                      ? 'Todas las sedes'
                      : access.siteIds.map((siteId) => siteNameById.get(siteId) ?? '—').join(', ')}
                  </TableCell>
                  <TableCell className="align-top">
                    {access.dailyQuantityLimit ?? 'Sin tope'}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-col text-xs text-slate-600">
                      <span>Desde {formatCalendarDate(access.validFrom)}</span>
                      <span>
                        {access.validTo === null
                          ? 'Sin límite'
                          : formatCalendarDate(access.validTo)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant={STATUS_BADGE_VARIANT[status]}>{STATUS_LABELS[status]}</Badge>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setModal({ mode: 'edit', access })}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        loading={revokeMutation.isPending && revokeMutation.variables === access.id}
                        onClick={() => {
                          if (
                            window.confirm(`¿Revocar el acceso al portal de "${access.fullName}"?`)
                          ) {
                            revokeMutation.mutate(access.id);
                          }
                        }}
                      >
                        Revocar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {modal?.mode === 'invite' ? (
        <PortalAccessInviteModal
          contacts={invitableContacts}
          sites={sites}
          submitting={inviteMutation.isPending}
          {...(inviteFormError === '' ? {} : { formError: inviteFormError })}
          onCancel={() => setModal(null)}
          onSubmit={async (input) => {
            await inviteMutation.mutateAsync(input).catch(() => undefined);
          }}
        />
      ) : null}

      {modal?.mode === 'activation-link' ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title="Enlace de activación"
          className="max-w-lg"
        >
          <div className="flex flex-col gap-4">
            <ActivationLinkNotice
              delivery={modal.result.delivery}
              expiresAt={modal.result.expiresAt}
            />
            <div>
              <Button type="button" variant="secondary" onClick={() => setModal(null)}>
                Listo
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {modal?.mode === 'edit' ? (
        <EditPortalAccessModal
          access={modal.access}
          sites={sites}
          submitting={updateMutation.isPending}
          {...(editFormError === '' ? {} : { formError: editFormError })}
          onCancel={() => setModal(null)}
          onSubmit={(input) => {
            void updateMutation
              .mutateAsync({ accessId: modal.access.id, input })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

interface EditPortalAccessModalProps {
  access: PortalAccessSummary;
  sites: { id: string; name: string }[];
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (input: UpdatePortalAccessInput) => void;
}

/**
 * `role` never appears here: `updatePortalAccessSchema` (`apps/api`) has
 * no `roleCode` field and rejects it outright (`.strict()`).
 */
function EditPortalAccessModal({
  access,
  sites,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: EditPortalAccessModalProps): JSX.Element {
  const [allSites, setAllSites] = useState(access.siteIds.length === 0);
  const [siteIds, setSiteIds] = useState<string[]>(access.siteIds);
  const [dailyQuantityLimit, setDailyQuantityLimit] = useState(
    access.dailyQuantityLimit === null ? '' : String(access.dailyQuantityLimit),
  );
  const [validFrom, setValidFrom] = useState(access.validFrom.slice(0, 10));
  const [validTo, setValidTo] = useState(
    access.validTo === null ? '' : access.validTo.slice(0, 10),
  );
  const [touched, setTouched] = useState(false);

  const validityOutOfOrder = touched && validTo !== '' && validTo < validFrom;

  function toggleSite(siteId: string): void {
    setSiteIds((current) =>
      current.includes(siteId) ? current.filter((id) => id !== siteId) : [...current, siteId],
    );
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setTouched(true);

    if (validTo !== '' && validTo < validFrom) {
      return;
    }

    const newSiteIds = allSites ? [] : siteIds;
    const newLimit = dailyQuantityLimit === '' ? null : Number(dailyQuantityLimit);
    const newValidTo = validTo === '' ? null : validTo;
    const changes: UpdatePortalAccessInput = {};

    if (JSON.stringify(newSiteIds) !== JSON.stringify(access.siteIds)) {
      changes.siteIds = newSiteIds;
    }

    if (newLimit !== access.dailyQuantityLimit) {
      changes.dailyQuantityLimit = newLimit;
    }

    if (validFrom !== access.validFrom.slice(0, 10)) {
      changes.validFrom = validFrom;
    }

    if (newValidTo !== (access.validTo === null ? null : access.validTo.slice(0, 10))) {
      changes.validTo = newValidTo;
    }

    onSubmit(changes);
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title={`Editar acceso de ${access.fullName}`}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Sedes</legend>
          <Checkbox
            label="Todas las sedes"
            checked={allSites}
            onChange={(event) => setAllSites(event.target.checked)}
          />
          {!allSites ? (
            <div className="flex flex-col gap-2 pl-6">
              {sites.map((site) => (
                <Checkbox
                  key={site.id}
                  label={site.name}
                  checked={siteIds.includes(site.id)}
                  onChange={() => toggleSite(site.id)}
                />
              ))}
            </div>
          ) : null}
        </fieldset>

        <Field
          label="Tope de cantidad diaria"
          name="dailyQuantityLimit"
          type="number"
          min={0}
          value={dailyQuantityLimit}
          onChange={(event) => setDailyQuantityLimit(event.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Vigente desde"
            name="validFrom"
            type="date"
            value={validFrom}
            onChange={(event) => setValidFrom(event.target.value)}
          />
          <Field
            label="Vigente hasta"
            name="validTo"
            type="date"
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
          />
        </div>
        {validityOutOfOrder ? (
          <p role="alert" className="text-xs text-red-600">
            La fecha de fin debe ser posterior o igual a la de inicio
          </p>
        ) : null}

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            Guardar cambios
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
